import React from 'react';
import { Box, Typography } from '@mui/material';
import moment from 'moment';
import useEmployeeBackgroundCheckService, {
  UPDATE_BACKGROUND_CHECK,
} from 'domain/people/employee/PersonalLegal/backgroundcheck/shared/employeeBackgroundCheckService';
import { BackgroundCheckAttachmentUploaderInput } from 'domain/people/employee/PersonalLegal/backgroundcheck';
import { AttachmentsListField } from 'domain/shared/artifact/field';
import { BackgroundCheckItemProps } from './types';
import { FormShowTemplate, useFormShowContext, DateInput, DEFAULT_ERROR_MESSAGE } from 'framework/form';
import BackgroundCheckResultDropdown from '../input/BackgroundCheckResultDropdown';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import EditFormShowButton from '../../../shared/button/showEdit/EditFormShowButton';
import FormActionButtons from '../../../shared/button/showEdit/FormActionButtons';

const BackgroundCheckItem: React.FC<BackgroundCheckItemProps> = ({
  backgroundCheck,
  onUpdate,
  onStartEdit,
  disabled,
}) => {
  const { updateBackgroundCheck, getValidationSchema } = useEmployeeBackgroundCheckService();
  const { addMessage } = useMessageService(`backgroundCheck-${backgroundCheck.id}`);

  const handleErrorsOnSubmit = () => {
    addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
  };

  const handleSubmit = async (values: any) => {
    await updateBackgroundCheck(backgroundCheck.id, values);
    onUpdate();
    return values;
  };

  const ShowContent = () => {
    const checkDate = backgroundCheck?.performedAt ? moment(backgroundCheck.performedAt).format('DD.MM.YYYY') : '-';
    const result = backgroundCheck?.result || '-';
    const attachmentsIds = backgroundCheck?.attachmentsIds || [];

    return (
      <Box sx={{ mb: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="subtitle1">
            Background check {result?.toLowerCase().replace(/_/g, ' ')} on {checkDate}
          </Typography>

          <EditFormShowButton requiredPermission={UPDATE_BACKGROUND_CHECK} disabled={disabled} onClick={onStartEdit} />
        </Box>

        <AttachmentsListField value={attachmentsIds} />
      </Box>
    );
  };

  const EditContent = () => {
    return (
      <Box sx={{ mb: 2, mt: 2, display: 'flex' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <DateInput source="performedAt" label="Date" />
            <BackgroundCheckResultDropdown source="result" label="Result" />
          </Box>
          <BackgroundCheckAttachmentUploaderInput source="attachmentsIds" label="Attachments" />
        </Box>
        <Box>
          <FormActionButtons />
        </Box>
      </Box>
    );
  };

  const FormContent = () => {
    const { isEditMode } = useFormShowContext();
    return isEditMode ? <EditContent /> : <ShowContent />;
  };

  return (
    <FormShowTemplate
      getValidationSchemaFunc={getValidationSchema}
      submitFormFunc={handleSubmit}
      getDataFunc={() => Promise.resolve(backgroundCheck)}
      messageContext={`backgroundCheck-${backgroundCheck.id}`}
      handleErrorsOnSubmit={handleErrorsOnSubmit}
    >
      <FormContent />
    </FormShowTemplate>
  );
};

export default BackgroundCheckItem;
